import type {
  PartialPageSlug,
  CTATarget,
  CTAGtmMetaType,
} from '@/components/cms/schemas';
import { CTATargetOption } from '@/components/cms/schemas';
import { gtmCMSCta } from '@/utils/events';
import { getGlobalStore } from '@/utils/store';
import { containsURI, getHashAndStripUrl } from '@/utils/url';
import { useNavigate } from '@tanstack/react-router';

interface UseCTA {
  target: CTATarget;
  url: string;
  drawerContent: PartialPageSlug | null;
  enableGTM: boolean;
  gtmCta: CTAGtmMetaType;
}
export function useCTA({
  target,
  url,
  drawerContent,
  enableGTM,
  gtmCta,
}: UseCTA) {
  const navigate = useNavigate();
  const { setOpenChat, setAutoOpen } = getGlobalStore();

  const handleInternalClick = () => {
    try {
      const { finalURL, hash } = getHashAndStripUrl(url);

      if (containsURI(finalURL)) {
        window.location.href = finalURL;
        return;
      }

      navigate({
        to: finalURL ? finalURL : '.',
        search: (search) => ({ ...search, drawer: undefined }),
        ...(hash ? { hash: hash } : {}),
        hashScrollIntoView: {
          block: 'center',
        },
      });
    } catch (e) {
      // TODO: Log error to sentry so we can track this issue
      console.error('link is not a valid internal link', e);
      window.location.href = url;
    }
  };

  const handleDrawerClick = () => {
    navigate({
      to: '.',
      search: (search) => ({ ...search, drawer: drawerContent }),
    });
  };

  const handleChatbotClick = () => {
    setOpenChat(true);
    setAutoOpen(true);
  };

  const onClick = (
    event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ) => {
    if (enableGTM) {
      gtmCMSCta(gtmCta);
    }
    if (target === CTATargetOption.Enum._blank) {
      window.open(url);
      return;
    }
    event.preventDefault();

    if (target === CTATargetOption.Enum.chatbot) {
      handleChatbotClick();
      return;
    }
    if (target === CTATargetOption.Enum.drawer && drawerContent) {
      handleDrawerClick();
      return;
    }

    handleInternalClick();
  };

  return onClick;
}
